'use client';

import type { IColorVariationAttribute } from '@/services/models/Product/variation-attributes';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { generateSwatchBackgroundStyles } from '@/react/utils/product-utils';
import { msgf } from '@/services/isomorphic/I18NService';
import { Link } from '../../../core-ui/Link';
import { HoverIntent } from '../../../utility/HoverIntent';

import S from './styles.base.module.scss';
import { product_tiles_selectColor } from "@/lang/__generated__/ahnu/product_tiles_selectColor";

export interface IColorOptionProps {
  /** The color to display. */
  color: IColorVariationAttribute;

  /** Link to navigate to when clicked. */
  href: string | URL;

  /** Callback to run on hover. */
  onHover?: () => void;

  /** Mouse leave action. */
  onLeave?: () => void;
}

/** An option or "bubble" for the color selector on the product tile. */
export const ColorOption: FunctionComponent<IColorOptionProps> = ({
  color,
  href,
  onHover,
  onLeave
}) => {
  // Generate style for hex value (single color or multi-color gradient)
  const swatchStyle = useMemo(
    () => generateSwatchBackgroundStyles(color.hexValue, color.primaryImage),
    [color.hexValue, color.primaryImage]
  );

  return (
    <HoverIntent onHoverIn={onHover} onHoverOut={onLeave} delay={0}>
      <Link
        href={href}
        aria-label={msgf(product_tiles_selectColor, {
          colorName: color.name
        })}
      >
        <div className={S.colorOption} style={swatchStyle} />
      </Link>
    </HoverIntent>
  );
};

/** Holds the color options of a product tile. */
const ColorSelector: FunctionComponent<PropsWithChildren> = ({ children }) => {
  return <div className={S.colorSelector}>{children}</div>;
};

export default ColorSelector;
